import { createContext, useContext, useState, useEffect, type ReactNode } from 'react';
import type { Produto, ItemCarrinho, CarrinhoContextType } from '../types/loja';

const CartContext = createContext<CarrinhoContextType | undefined>(undefined);

export function CartProvider({ children }: { children: ReactNode }) {
  const [itens, setItens] = useState<ItemCarrinho[]>(() => {
    // Carregar carrinho do localStorage ao iniciar
    const carrinhoSalvo = localStorage.getItem('carrinho');
    return carrinhoSalvo ? JSON.parse(carrinhoSalvo) : [];
  });

  // Salvar carrinho no localStorage sempre que mudar
  useEffect(() => {
    localStorage.setItem('carrinho', JSON.stringify(itens));
  }, [itens]);

  // Adicionar item ao carrinho
  const adicionarItem = (produto: Produto, quantidade: number = 1) => {
    setItens((itensAtuais) => {
      // Verificar se o produto já está no carrinho
      const itemExistente = itensAtuais.find((item) => item.produto.id === produto.id);

      if (itemExistente) {
        // Atualizar quantidade do item existente
        return itensAtuais.map((item) =>
          item.produto.id === produto.id
            ? { ...item, quantidade: item.quantidade + quantidade }
            : item
        );
      } else {
        // Adicionar novo item
        return [...itensAtuais, { produto, quantidade }];
      }
    });
  };

  // Remover item do carrinho
  const removerItem = (produtoId: string) => {
    setItens((itensAtuais) => itensAtuais.filter((item) => item.produto.id !== produtoId));
  };

  // Atualizar quantidade de um item
  const atualizarQuantidade = (produtoId: string, quantidade: number) => {
    if (quantidade <= 0) {
      removerItem(produtoId);
      return;
    }

    setItens((itensAtuais) =>
      itensAtuais.map((item) =>
        item.produto.id === produtoId ? { ...item, quantidade } : item
      )
    );
  };

  // Limpar carrinho
  const limparCarrinho = () => {
    setItens([]);
  };

  // Calcular total do carrinho
  const total = itens.reduce((acc, item) => {
    const preco = item.produto.preco_promocional || item.produto.preco;
    return acc + preco * item.quantidade;
  }, 0);

  // Calcular quantidade total de itens
  const quantidadeTotal = itens.reduce((acc, item) => acc + item.quantidade, 0);

  const value: CarrinhoContextType = {
    itens,
    adicionarItem,
    removerItem,
    atualizarQuantidade,
    limparCarrinho,
    total,
    quantidadeTotal,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

// Hook para usar o carrinho
export function useCart() {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart deve ser usado dentro de um CartProvider');
  }
  return context;
}
